import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Modal from "components/modal/modal";
import FormInput from "components/input/form-input";
import FormSelect from "components/input/form-select";
import FormTextarea from "components/input/form-textarea";
import FormSuccess from "components/modal/form-success";
import { FORMSPREE_KEYS, formspreeUrl } from "config/formspree";

const inquiryTypeOptions = [
  "General Inquiry",
  "Partnership",
  "Recruitment",
  "Training",
  "Project",
  "Other",
];

const schema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Enter a valid email").required("Email is required"),
  phone: yup.string().required("Phone number is required"),
  inquiryType: yup.string(),
  additional: yup.string(),
});

type FormValues = yup.InferType<typeof schema>;

interface Props {
  open: boolean;
  handleClose: () => void;
  image?: string;
}

const ContactUsModal = ({ open, handleClose, image }: Props) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema as any),
  });

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setSubmitError(null);
    try {
      const res = await fetch(formspreeUrl(FORMSPREE_KEYS.contact), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          inquiryType: data.inquiryType,
          additionalInformation: data.additional,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
      reset();
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    reset();
    setSubmitted(false);
    setSubmitError(null);
    handleClose();
  };

  return (
    <Modal open={open} handleClose={onClose} className="w-[800px] max-w-[95vw]">
      {submitted ? (
        <FormSuccess
          title="Message Sent!"
          message="Thank you for reaching out. Our team will get back to you within 24 hours."
          onClose={onClose}
        />
      ) : (
        <div className="flex min-h-[560px]">
          {/* Left — image panel (hidden on mobile) */}
          <div className="hidden md:flex w-[42%] shrink-0 bg-[#F0F0F0] items-center justify-center p-8 rounded-l-2xl">
            {image ? (
              <img
                src={image}
                alt="contact"
                className="w-full max-w-[280px] object-contain"
              />
            ) : (
              <div className="w-full max-w-[280px] aspect-square rounded-xl bg-gray-200 flex items-center justify-center text-gray-400 text-sm">
                Image
              </div>
            )}
          </div>

          {/* Right — form panel */}
          <div className="flex flex-col flex-1 p-8 overflow-y-auto scrollbar-none">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col h-full gap-5"
            >
              {/* Header */}
              <div className="flex flex-col gap-1 pr-8">
                <h2 className="text-[28px] font-bold text-[#1A1A2E] leading-tight">
                  Get in Touch
                </h2>
                <p className="text-sm text-[#6B6B6B]">
                  We are excited to discuss how we can help you. So our team is
                  well-prepared, please send us a message here. We are available 24/7
                </p>
              </div>

              {/* Fields */}
              <FormInput
                label="Name"
                placeholder="Jenny Smith"
                register={register("name")}
                error={errors.name}
              />
              <FormInput
                label="Email Address"
                type="email"
                placeholder="jenny@gmail.com"
                register={register("email")}
                error={errors.email}
              />
              <FormInput
                label="Phone number"
                placeholder="+1 234 567 8900"
                register={register("phone")}
                error={errors.phone}
              />
              <Controller
                name="inquiryType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelect
                    label="Inquiry type"
                    placeholder="Select an option"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    options={inquiryTypeOptions}
                    error={fieldState.error}
                  />
                )}
              />
              <FormTextarea
                label="Additional Information"
                placeholder="Write your information"
                register={register("additional")}
                error={errors.additional}
              />

              {submitError && (
                <p className="text-sm text-red-500 text-center">{submitError}</p>
              )}

              {/* Submit — full width */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-[#CBA135] text-[#1A1A2E] font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60 mt-auto"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ContactUsModal;
